import { useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { errMsg, rupees } from "@/lib/api";
import { useAdminApi } from "@/lib/admin";
import { useLoad, PageHead, DataTable, Td, NativeSelect } from "@/components/admin/kit";

const QUALS = ["Below 10th", "10th Pass", "12th Pass", "Diploma / ITI", "Graduate", "Post Graduate"];
const BLANK = { title: "", vacancies: 0, salary_min: 0, salary_max: 0, incentives: "Performance-Based Incentives", description: "", qualifications: "", skills: [], responsibilities: [], deadline: "", applications_open: true, is_visible: true, min_qualification: "10th Pass", min_age: "", max_age: "", require_photo: false, require_certificates: false, sort_order: 0 };

const JobEditor = ({ job, onClose }) => {
  const call = useAdminApi();
  const [j, setJ] = useState({ ...BLANK, ...job, skills: (job?.skills || []).join("\n"), responsibilities: (job?.responsibilities || []).join("\n") });
  const set = (k) => (e) => setJ({ ...j, [k]: e.target.value });
  const save = async () => {
    const body = { ...j, vacancies: +j.vacancies, salary_min: +j.salary_min, salary_max: +j.salary_max, sort_order: +j.sort_order,
      min_age: j.min_age ? +j.min_age : null, max_age: j.max_age ? +j.max_age : null, min_qualification: j.min_qualification || null, deadline: j.deadline || null,
      skills: j.skills.split("\n").map((s) => s.trim()).filter(Boolean), responsibilities: j.responsibilities.split("\n").map((s) => s.trim()).filter(Boolean) };
    try {
      await (job?.id ? call("put", `/admin/jobs/${job.id}`, body) : call("post", "/admin/jobs", body));
      toast.success("Vacancy saved");
      onClose(true);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const F = ({ k, l, t = "text" }) => <div className="space-y-1"><Label>{l}</Label><Input type={t} value={j[k] ?? ""} onChange={set(k)} data-testid={`job-edit-${k}`} /></div>;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="job-editor-dialog">
        <DialogHeader><DialogTitle>{job?.id ? "Edit vacancy" : "New vacancy"}</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">{F({ k: "title", l: "Job title" })}</div>
          {F({ k: "vacancies", l: "Total vacancies", t: "number" })}{F({ k: "deadline", l: "Application deadline", t: "date" })}
          {F({ k: "salary_min", l: "Monthly salary min (₹)", t: "number" })}{F({ k: "salary_max", l: "Monthly salary max (₹)", t: "number" })}
          <div className="sm:col-span-2">{F({ k: "incentives", l: "Additional benefits" })}</div>
          <div className="space-y-1 sm:col-span-2"><Label>Job description</Label><Textarea value={j.description} onChange={set("description")} data-testid="job-edit-description" /></div>
          <div className="space-y-1 sm:col-span-2"><Label>Required qualifications (text)</Label><Textarea value={j.qualifications} onChange={set("qualifications")} data-testid="job-edit-qualifications" /></div>
          <div className="space-y-1"><Label>Skills (one per line)</Label><Textarea rows={4} value={j.skills} onChange={set("skills")} data-testid="job-edit-skills" /></div>
          <div className="space-y-1"><Label>Responsibilities (one per line)</Label><Textarea rows={4} value={j.responsibilities} onChange={set("responsibilities")} data-testid="job-edit-responsibilities" /></div>
          <div className="space-y-1"><Label>Minimum qualification (enforced)</Label><NativeSelect value={j.min_qualification || ""} onChange={(v) => setJ({ ...j, min_qualification: v })} placeholder="None" options={QUALS} testid="job-edit-min-qualification" /></div>
          <div className="grid grid-cols-2 gap-2">{F({ k: "min_age", l: "Min age", t: "number" })}{F({ k: "max_age", l: "Max age", t: "number" })}</div>
          {F({ k: "sort_order", l: "Display order", t: "number" })}
          <div className="space-y-2 pt-2 sm:col-span-2">
            {[["applications_open", "Accept applications"], ["is_visible", "Show on website"], ["require_photo", "Photograph required"], ["require_certificates", "Certificates required"]].map(([k, l]) => (
              <label key={k} className="flex items-center gap-3 text-sm"><Switch checked={!!j[k]} onCheckedChange={(v) => setJ({ ...j, [k]: v })} data-testid={`job-edit-${k}`} />{l}</label>
            ))}
          </div>
        </div>
        <Button onClick={save} data-testid="job-save-button">Save vacancy</Button>
      </DialogContent>
    </Dialog>
  );
};

export default function Jobs({ isAdmin }) {
  const [jobs, reload] = useLoad("/admin/jobs");
  const [edit, setEdit] = useState(null);
  const total = (jobs || []).filter((j) => j.is_visible).reduce((s, j) => s + j.vacancies, 0);
  return (
    <div>
      <PageHead title="Vacancy management">{isAdmin && <Button onClick={() => setEdit({})} data-testid="job-add-button"><Plus className="h-4 w-4" /> Add position</Button>}</PageHead>
      <p className="mb-3 text-sm text-slate-600">Total advertised vacancies: <strong className="font-mono-num" data-testid="admin-total-vacancies">{total.toLocaleString("en-IN")}</strong></p>
      <DataTable head={["Title", "Vacancies", "Salary / month", "Deadline", "Min. qualification", "Paid applications", "Status", ""]} testid="jobs-table">
        {jobs?.map((j) => (
          <tr key={j.id} data-testid={`job-row-${j.id}`}>
            <Td className="font-semibold">{j.title}</Td><Td className="font-mono-num">{j.vacancies.toLocaleString("en-IN")}</Td>
            <Td className="font-mono-num">{rupees(j.salary_min)} – {rupees(j.salary_max)}</Td><Td>{j.deadline || "—"}</Td><Td>{j.min_qualification || "—"}</Td>
            <Td>{j.application_count}</Td><Td>{j.applications_open ? "Open" : "Closed"}{!j.is_visible && " · Hidden"}</Td>
            <Td>{isAdmin && <Button size="sm" variant="outline" onClick={() => setEdit(j)} data-testid={`job-edit-button-${j.id}`}>Edit</Button>}</Td>
          </tr>
        ))}
      </DataTable>
      {edit && <JobEditor job={edit.id ? edit : null} onClose={(changed) => { setEdit(null); if (changed) reload(); }} />}
    </div>
  );
}
